/**
 * Order arrays are borrowed without mutation and can be returned by identity.
 * The reducer assigns the result directly to mutable Redux collections, so keep
 * the mutable input/output contract rather than copying arrays just for typing.
 */
interface CollectionDiff<Value> {
  ids: string[];
  valuesById: Record<string, Value>;
  changedIds: readonly string[];
  readValue: (id: string) => Value | undefined;
  readOrder: () => string[];
  orderingChanged: boolean;
  projectedIds?: readonly string[];
}

function projectValue<Value>(collection: CollectionDiff<Value>, id: string): boolean {
  const value = collection.readValue(id);

  if (value !== undefined) {
    collection.valuesById[id] = value;
    return true;
  }

  delete collection.valuesById[id];
  return false;
}

export function applyCollectionDiffLegacy<Value>(
  collection: CollectionDiff<Value>
): string[] {
  let ids = collection.ids;
  let needsOrder = collection.orderingChanged;

  for (const id of collection.changedIds) {
    if (projectValue(collection, id)) {
      if (!ids.includes(id)) {
        needsOrder = true;
      }
    } else {
      ids = ids.filter((existingId) => existingId !== id);
    }
  }

  if (collection.projectedIds !== undefined) {
    return [...collection.projectedIds];
  }

  return needsOrder ? collection.readOrder() : ids;
}

/**
 * Ordered membership can differ from the value map during deferred projection.
 * Index only changed IDs: bulk bookkeeping is O(N + K), with O(K) lookup storage.
 * The returned order is shared for value-only updates and replaced only when needed.
 */
export function applyCollectionDiffLinear<Value>(
  collection: CollectionDiff<Value>
): string[] {
  if (collection.projectedIds !== undefined || collection.orderingChanged) {
    for (const id of collection.changedIds) {
      projectValue(collection, id);
    }

    return collection.projectedIds !== undefined
      ? [...collection.projectedIds]
      : collection.readOrder();
  }

  /**
   * Empty and single-ID diffs need no lookup allocation or second order traversal.
   */
  if (collection.changedIds.length === 0) {
    return collection.ids;
  }

  if (collection.changedIds.length === 1) {
    const id = collection.changedIds[0];
    if (!projectValue(collection, id)) {
      return collection.ids.filter((existingId) => existingId !== id);
    }

    return collection.ids.includes(id) ? collection.ids : collection.readOrder();
  }

  const pendingIds = new Set<string>();
  const deletedIds = new Set<string>();

  for (const id of collection.changedIds) {
    if (projectValue(collection, id)) {
      pendingIds.add(id);
    } else {
      deletedIds.add(id);
    }
  }

  if (pendingIds.size > 0) {
    for (const id of collection.ids) {
      pendingIds.delete(id);
      if (pendingIds.size === 0) {
        break;
      }
    }
  }

  if (pendingIds.size > 0) {
    return collection.readOrder();
  }

  return deletedIds.size > 0
    ? collection.ids.filter((id) => !deletedIds.has(id))
    : collection.ids;
}
